/**
 * Cart Gift Wrap Manager - Moon Moon Agency
 * Gère le bloc "Emballage cadeau" sur chaque item du panier (v2 — produit reel) :
 * - Toggle checkbox CHECK   → POST /cart/add.js du produit emballage (variant id expose via
 *                              data-giftwrap-variant-id sur [data-cart-products]) avec
 *                              line item property _for_line_key = key du parent.
 *                              Le wrap item est ainsi attache au parent via ce link.
 * - Toggle checkbox UNCHECK → POST /cart/change.js avec qty 0 sur le wrap line key.
 * - Bouton "Valider"        → POST /cart/change.js pour mettre a jour _giftwrap_message
 *                              avec le contenu actuel du textarea. Si pas encore de wrap
 *                              line (race condition apres check rapide), on add avec le message.
 * - Bouton Reduire          → masque le body sans changer le state Shopify.
 * - Bouton X (remove)       → uncheck + remove wrap.
 *
 * Convention :
 * - Le wrap item est filtre de l'affichage du cart cote Liquid (skip dans la for loop des items)
 *   grace a sa property _for_line_key. Il reste visible au checkout (par design).
 * - Le textarea ne sync PLUS automatiquement (debounce supprime). L'utilisateur doit cliquer
 *   "Valider" pour envoyer le message.
 */

/*********************************************************************************************
 * 🎁 CART GIFT WRAP MANAGER
 */
const CartGiftWrapManager = {
	config: {
		selectors: {
			block:       '[data-giftwrap-block]',
			toggle:      '[data-giftwrap-toggle]',
			body:        '[data-giftwrap-body]',
			textarea:    '[data-giftwrap-textarea]',
			counter:     '[data-giftwrap-counter]',
			collapse:    '[data-giftwrap-collapse]',
			validate:    '[data-giftwrap-validate]',
			remove:      '[data-giftwrap-remove]',
			cartProducts:'[data-cart-products]',
		},
	},

	state: {
		ready: false,
	},

	init() {
		this._bindEvents();
		this._refreshAllCounters();
		this.state.ready = true;
		window.log('info', '🎁 [CART_GIFTWRAP]', 'Initialized');
	},

	/* Récupère le variant_id du produit Emballage configure en section.
	 * Cherche au moment de l'appel (le DOM peut etre re-render apres un cart refresh). */
	_getWrapVariantId() {
		const wrap = document.querySelector(this.config.selectors.cartProducts);
		return wrap?.dataset.giftwrapVariantId || '';
	},

	/* Récupère les labels traduits des line item properties du wrap (Pour / Variante / Message).
	 * Lus depuis data-i18n-giftwrap-* sur [data-cart-products] (i18n via Liquid).
	 * Les keys sont visibles au checkout Shopify (côté merchant + customer), donc doivent
	 * suivre la langue du store. */
	_getPropLabels() {
		const wrap = document.querySelector(this.config.selectors.cartProducts);
		return {
			for:     wrap?.dataset.i18nGiftwrapFor     || 'Pour',
			variant: wrap?.dataset.i18nGiftwrapVariant || 'Variante',
			message: wrap?.dataset.i18nGiftwrapMessage || 'Message',
		};
	},

	_bindEvents() {
		document.addEventListener('change', (e) => {
			if (e.target.matches(this.config.selectors.toggle)) {
				this._handleToggleChange(e.target);
			}
		});

		document.addEventListener('input', (e) => {
			if (e.target.matches(this.config.selectors.textarea)) {
				this._updateCounter(e.target);
			}
		});

		document.addEventListener('click', (e) => {
			const collapse = e.target.closest(this.config.selectors.collapse);
			if (collapse) { this._handleCollapseClick(collapse); return; }

			const validate = e.target.closest(this.config.selectors.validate);
			if (validate) { this._handleValidateClick(validate); return; }

			const remove = e.target.closest(this.config.selectors.remove);
			if (remove) { this._handleRemoveClick(remove); }
		});

		document.addEventListener('cart:refreshed', () => this._refreshAllCounters());
	},

	_handleToggleChange(checkbox) {
		const block = checkbox.closest(this.config.selectors.block);
		if (!block) return;
		const parentLineKey = block.dataset.lineKey;
		const parentTitle = block.dataset.parentTitle || '';
		const parentVariantTitle = block.dataset.parentVariantTitle || '';
		const wrapLineKey = block.dataset.wrapLineKey;
		const body = block.querySelector(this.config.selectors.body);

		if (checkbox.checked) {
			if (body) body.hidden = false;
			this._addWrap(parentLineKey, parentTitle, parentVariantTitle, '');
		} else {
			if (body) body.hidden = true;
			if (wrapLineKey) {
				this._removeWrap(wrapLineKey);
			}
		}
	},

	_handleCollapseClick(button) {
		const block = button.closest(this.config.selectors.block);
		const body = block?.querySelector(this.config.selectors.body);
		const toggle = block?.querySelector(this.config.selectors.toggle);
		if (!body) return;
		/* Reduire = juste cacher le body, on garde le wrap et le message */
		body.hidden = true;
		if (toggle) toggle.focus();
	},

	_handleValidateClick(button) {
		const block = button.closest(this.config.selectors.block);
		if (!block) return;
		const parentLineKey = block.dataset.lineKey;
		const parentTitle = block.dataset.parentTitle || '';
		const parentVariantTitle = block.dataset.parentVariantTitle || '';
		const wrapLineKey = block.dataset.wrapLineKey;
		const textarea = block.querySelector(this.config.selectors.textarea);
		const message = textarea?.value || '';

		/* Feedback visuel immediat */
		if (textarea) textarea.disabled = true;
		button.hidden = true;
		block.classList.add('mm-cart-item-giftwrap--locked');

		if (wrapLineKey) {
			this._updateWrapMessage(wrapLineKey, parentLineKey, parentTitle, parentVariantTitle, message);
		} else {
			/* Race case : Valider cliqué avant que le cart soit rafraîchi apres l'add. */
			this._addWrap(parentLineKey, parentTitle, parentVariantTitle, message);
		}
	},

	_handleRemoveClick(button) {
		const block = button.closest(this.config.selectors.block);
		if (!block) return;
		const toggle = block.querySelector(this.config.selectors.toggle);
		const textarea = block.querySelector(this.config.selectors.textarea);
		const wrapLineKey = block.dataset.wrapLineKey;
		const body = block.querySelector(this.config.selectors.body);

		if (toggle) toggle.checked = false;
		if (textarea) textarea.value = '';
		if (body) body.hidden = true;

		if (wrapLineKey) this._removeWrap(wrapLineKey);
	},

	_updateCounter(textarea) {
		const block = textarea.closest(this.config.selectors.block);
		const counter = block?.querySelector(this.config.selectors.counter);
		if (!counter) return;
		const max = parseInt(textarea.getAttribute('maxlength') || '0', 10);
		const current = textarea.value.length;
		const remaining = Math.max(0, max - current);
		const original = counter.textContent || '';
		const match = original.match(/\d+\s+(.+)/);
		const suffix = match ? match[1] : (counter.dataset.suffix || 'caractères');
		counter.textContent = `${remaining} ${suffix}`;
	},

	_refreshAllCounters() {
		document.querySelectorAll(this.config.selectors.textarea).forEach((ta) => this._updateCounter(ta));
	},

	/* ----- AJAX cart operations ----- */

	/* Construit les properties du wrap item.
	 * - _for_line_key : prefix _ = CACHE au checkout. Lien technique entre le wrap et son parent.
	 * - Pour : VISIBLE au checkout → nom du produit parent (info merchant + logistique).
	 * - Variante : VISIBLE au checkout → nom de la variante (ex 'Noir / 51-17') pour que la
	 *   logistique sache QUELLE variante emballer si plusieurs produits au panier. Omis si Default Title.
	 * - Message : VISIBLE au checkout → contenu cadeau (envoye que si non-vide). */
	_buildWrapProperties(parentLineKey, parentTitle, parentVariantTitle, message) {
		const props = {
			_for_line_key: parentLineKey,
		};
		const labels = this._getPropLabels();
		if (parentTitle) props[labels.for] = parentTitle;
		if (parentVariantTitle) props[labels.variant] = parentVariantTitle;
		if (message) props[labels.message] = message;
		return props;
	},

	_addWrap(parentLineKey, parentTitle, parentVariantTitle, message) {
		const variantId = this._getWrapVariantId();
		if (!variantId) {
			window.log('warn', '🎁 [CART_GIFTWRAP]', 'No giftwrap variant id configured');
			return;
		}
		this._sendCartCommand('/cart/add.js', {
			id: variantId,
			quantity: 1,
			properties: this._buildWrapProperties(parentLineKey, parentTitle, parentVariantTitle, message),
		});
	},

	_removeWrap(wrapLineKey) {
		this._sendCartCommand('/cart/change.js', { id: wrapLineKey, quantity: 0 });
	},

	_updateWrapMessage(wrapLineKey, parentLineKey, parentTitle, parentVariantTitle, message) {
		/* /cart/change.js properties REMPLACE toutes les properties — on re-passe tout
		 * pour ne pas perdre _for_line_key / Pour / Variante. */
		this._sendCartCommand('/cart/change.js', {
			id: wrapLineKey,
			properties: this._buildWrapProperties(parentLineKey, parentTitle, parentVariantTitle, message),
		});
	},

	/* Envoie une commande cart en POST avec `sections` pour recevoir le HTML refresh
	 * du cart drawer dans la response, puis trigger mmRefreshCartUI(data). Aligne avec
	 * le pattern utilise par mm_lens_tunnel.js et mm_quick_view.js. */
	async _sendCartCommand(url, body) {
		try {
			const cartSectionId = (typeof window.mmGetCartSectionId === 'function') ? window.mmGetCartSectionId() : '';
			if (cartSectionId) body.sections = cartSectionId;

			const res = await fetch(url, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
				body: JSON.stringify(body),
			});
			if (!res.ok) {
				const err = await res.json().catch(() => ({}));
				throw new Error(err.description || err.message || `HTTP ${res.status}`);
			}
			const data = await res.json();
			window.log('info', '🎁 [CART_GIFTWRAP]', `${url} success`);

			if (typeof window.mmRefreshCartUI === 'function') {
				window.mmRefreshCartUI(data);
			}
			window.emit('global:cart-updated');
		} catch (err) {
			window.log('error', '🎁 [CART_GIFTWRAP]', `${url} failed`, { error: err.message });
		}
	},
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => CartGiftWrapManager.init());
} else {
	CartGiftWrapManager.init();
}

window.MMCartGiftWrap = CartGiftWrapManager;
